import type {
  ApercuDuConcours,
  DemandeDeVerification,
  Faculty,
  TagNode,
  TwoFactor,
  User,
} from '@upfac/api-contract'
import { api } from './client'

// RG-A5 à RG-A7 — les formes viennent du contrat GÉNÉRÉ depuis le backend.
// Les redéclarer ici créerait deux vérités : le jour où l'API change, c'est
// la copie qui ment, et TypeScript ne le voit pas.
export type { ApercuDuConcours, DemandeDeVerification, Faculty, TagNode, TwoFactor }

/** Le compte du candidat connecté — `User` côté contrat. */
export type Me = User

// ADR-05 — le jeton CSRF est obtenu et renvoyé par `client.ts`, avant toute
// écriture et pour TOUTES les routes. Le faire ici, route par route, laissait
// passer celles qu'on oubliait — et ne renvoyait pas l'en-tête X-XSRF-TOKEN.

// ── L'inscription en trois temps — [DECISIONS §159] ───────────────────────
//
// **Il n'y a plus de `register()`.** Un compte ne naît plus d'un formulaire :
// il naît d'une adresse PROUVÉE. Les trois appels ci-dessous suivent l'ordre
// de la décision, et aucun ne peut se sauter — le serveur refuse le suivant
// tant que le précédent n'a pas abouti.
//
// **Rien à voir avec RG-1.4** (`twoFactor`, plus bas). Le deuxième facteur
// demande « est-ce bien toi qui te connectes » ; ici on demande « ce courriel
// est-il à toi ».

/**
 * Étape 1 — l'adresse seule.
 *
 * Répond **202 dans tous les cas**, que l'adresse soit libre ou déjà inscrite.
 * L'écran ne doit donc rien déduire de la réponse : c'est le courriel reçu qui
 * diffère, et lui n'est lu que par le propriétaire de la boîte.
 */
export async function demanderUnCode(email: string): Promise<DemandeDeVerification> {
  const res = await api<{ data: DemandeDeVerification }>('/inscription/adresse', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })

  return res.data
}

/** « Je n'ai rien reçu » — le jeton suffit, l'adresse vient de la demande. */
export async function renvoyerLeCode(verification: string): Promise<DemandeDeVerification> {
  const res = await api<{ data: DemandeDeVerification }>('/inscription/renvoi', {
    method: 'POST',
    body: JSON.stringify({ verification }),
  })

  return res.data
}

/** Étape 2 — le code à six chiffres. */
export async function verifierLeCode(
  verification: string,
  code: string,
): Promise<DemandeDeVerification> {
  const res = await api<{ data: DemandeDeVerification }>('/inscription/code', {
    method: 'POST',
    body: JSON.stringify({ verification, code }),
  })

  return res.data
}

/**
 * Étape 3 — le compte.
 *
 * Aucune adresse ici : elle vient de la demande vérifiée. `phone` porte
 * **huit chiffres**, sans le 509 et sans le tiret d'affichage.
 */
export interface CreationDuCompte {
  verification: string
  name: string
  display_name: string
  phone: string
  password: string
  password_confirmation: string
  birth_year?: number
}

/**
 * Le compte créé, et l'état de sa session — [DECISIONS §317].
 *
 * `session_ouverte` dit si le candidat est DÉJÀ connecté. Il l'est presque
 * toujours ; le `false` est le repli du 20 août, quand l'ouverture échoue après
 * que le compte a été écrit. L'écran doit alors conduire à la connexion plutôt
 * que d'entrer dans une application où personne n'est authentifié.
 */
export interface CompteCree {
  me: Me
  sessionOuverte: boolean
}

export async function creerLeCompte(payload: CreationDuCompte): Promise<CompteCree> {
  const res = await api<{ data: Me; meta?: { session_ouverte?: boolean } }>('/inscription/compte', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

  // `?? false` et non `?? true` : en l'absence d'information, on conduit à la
  // connexion. Se tromper dans ce sens fait taper un mot de passe ; se tromper
  // dans l'autre fait entrer dans une application vide, sans rien dire.
  return { me: res.data, sessionOuverte: res.meta?.session_ouverte ?? false }
}

// ── Changer l'adresse d'un compte existant — §159 ─────────────────────────

/**
 * Lance le changement : code à la nouvelle adresse, **alerte à l'ancienne**.
 *
 * Le mot de passe est redemandé : une session ouverte n'est pas une preuve
 * d'identité.
 */
export async function demanderLeChangementDAdresse(
  password: string,
  email: string,
): Promise<DemandeDeVerification> {
  const res = await api<{ data: DemandeDeVerification }>('/me/adresse', {
    method: 'POST',
    body: JSON.stringify({ password, email }),
  })

  return res.data
}

/** La bascule n'a lieu qu'ici. Jusque-là, l'ancienne adresse reste active. */
export async function confirmerLeChangementDAdresse(
  verification: string,
  code: string,
): Promise<Me> {
  const res = await api<{ data: Me }>('/me/adresse/code', {
    method: 'POST',
    body: JSON.stringify({ verification, code }),
  })

  return res.data
}

// ── Mot de passe oublié — §159 ────────────────────────────────────────────

/** 202 que le compte existe ou non — l'écran ne doit rien en déduire. */
export async function motDePasseOublie(email: string): Promise<DemandeDeVerification> {
  const res = await api<{ data: DemandeDeVerification }>('/mot-de-passe/oublie', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })

  return res.data
}

export async function reinitialiserLeMotDePasse(
  verification: string,
  code: string,
  password: string,
): Promise<void> {
  await api<null>('/mot-de-passe/reinitialiser', {
    method: 'POST',
    body: JSON.stringify({
      verification,
      code,
      password,
      password_confirmation: password,
    }),
  })
}

/**
 * @param code code TOTP ou code de secours — RG-1.4. Omis pour les comptes
 *   sans deuxième facteur, c'est-à-dire la plupart.
 */
export async function login(email: string, password: string, code?: string): Promise<Me> {
  const res = await api<{ data: Me }>('/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, ...(code === undefined ? {} : { code }) }),
  })

  return res.data
}

/** RG-1.4 — le deuxième facteur, facultatif. */
export async function twoFactor(): Promise<TwoFactor> {
  const res = await api<{ data: TwoFactor }>('/me/two-factor')

  return res.data
}

/** Prépare un secret SANS activer : le candidat doit d'abord le confirmer. */
export async function prepareTwoFactor(): Promise<TwoFactor> {
  const res = await api<{ data: TwoFactor }>('/me/two-factor', { method: 'POST' })

  return res.data
}

export async function confirmTwoFactor(code: string): Promise<TwoFactor> {
  const res = await api<{ data: TwoFactor }>('/me/two-factor/confirm', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })

  return res.data
}

export async function disableTwoFactor(password: string): Promise<void> {
  await api<null>('/me/two-factor', { method: 'DELETE', body: JSON.stringify({ password }) })
}

export async function logout(): Promise<void> {
  await api<null>('/logout', { method: 'POST' })
}

/**
 * Confirme l'adresse depuis le lien reçu par courriel — RG-1.3.
 *
 * L'URL signée arrive telle quelle : la reconstruire invaliderait la
 * signature. On la rejoue.
 */
export async function verifyEmail(lienSigne: string): Promise<Me> {
  const res = await api<{ data: Me }>('', { method: 'GET' }, lienSigne)

  return res.data
}

export async function resendVerification(): Promise<void> {
  await api<null>('/email/verification-notification', { method: 'POST' })
}

export async function me(): Promise<Me> {
  const res = await api<{ data: Me }>('/me')

  return res.data
}

/** Ce qu'un candidat peut modifier sur son compte — F10. */
export interface PreferencesPatch {
  daily_goal_xp?: User['preferences']['daily_goal_xp']
  theme?: User['preferences']['theme']
  accent?: User['preferences']['accent']
  data_saver?: boolean
  /** RG-1.13bis — le pré-abonnement au choix du concours. */
  auto_follow_themes?: boolean
  leaderboard_opt_in?: boolean
  display_name?: string
}

export async function updatePreferences(patch: PreferencesPatch): Promise<Me> {
  const res = await api<{ data: Me }>('/me/preferences', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })

  return res.data
}

export async function faculties(): Promise<Faculty[]> {
  const res = await api<{ data: Faculty[] }>('/faculties')

  return res.data
}

/** Les facultés DÉJÀ choisies — sert à ne pas refaire subir l'accueil. */
export async function myFaculties(): Promise<Faculty[]> {
  const res = await api<{ data: Faculty[] }>('/me/faculties')

  return res.data
}

/**
 * @param options les FILIÈRES retenues, par slug de faculté — §232. Le serveur
 *   l'exige sur la principale quand elle en propose : sans elle, il départage
 *   par un heuristique, et il départageait mal.
 */
export async function chooseFaculties(
  slugs: string[],
  primary?: string,
  options: Record<string, string> = {},
): Promise<void> {
  await api<unknown>('/me/faculties', {
    method: 'PUT',
    body: JSON.stringify({ faculties: slugs, primary: primary ?? slugs[0], options }),
  })
}

export async function tagTree(scope: 'faculty' | 'all' = 'faculty'): Promise<TagNode[]> {
  const res = await api<{ data: TagNode[] }>(`/tags?facet=theme&scope=${scope}`)

  return res.data
}

export async function followTag(id: number): Promise<void> {
  await api<unknown>(`/tags/${id}/follow`, { method: 'PUT' })
}

export async function unfollowTag(id: number): Promise<void> {
  await api<unknown>(`/tags/${id}/follow`, { method: 'DELETE' })
}

/** RG-1.15 — sourdine temporaire, 14 jours au plus. Le thème reste suivi. */
export async function muteTag(id: number, days = 14): Promise<void> {
  await api<unknown>(`/tags/${id}/mute`, { method: 'PUT', body: JSON.stringify({ days }) })
}

export async function unmuteTag(id: number): Promise<void> {
  await api<unknown>(`/tags/${id}/mute`, { method: 'DELETE' })
}

/**
 * Le concours ACTIF — RG-1.12, RG-1.13, [DECISIONS §53].
 *
 * Un seul concours pilote le fil. En changer REMPLACE les thèmes suivis par
 * ceux du nouveau : le fil est une vue sur le corpus, pas une accumulation.
 */
export interface ConcoursDuCandidat {
  slug: string
  nom: string
  actif: boolean
  /** Ce que la bascule poserait — de quoi annoncer le remplacement avant. */
  themes_proposes: number
}

export function mesConcours(): Promise<{
  data: { concours: ConcoursDuCandidat[]; themes_suivis: number }
}> {
  return api<{ data: { concours: ConcoursDuCandidat[]; themes_suivis: number } }>('/me/concours')
}

/**
 * Ce qu'un concours du CATALOGUE ferait au fil, sans le faire — §264.
 *
 * `mesConcours` ne décrit que les concours déjà visés : l'écran où l'on en
 * coche un nouveau n'avait aucun chiffre à annoncer, et le remplacement se
 * découvrait après coup.
 *
 * @param filiere la FILIÈRE visée (§232), ou `null` pour une faculté à
 *   concours unique. Le serveur refuse une filière d'une autre faculté plutôt
 *   que de rendre un chiffre qui ne décrit rien.
 */
export async function apercuDuConcours(
  faculte: string,
  filiere: string | null,
): Promise<ApercuDuConcours> {
  const parametres = new URLSearchParams({ faculte })

  if (filiere !== null) {
    parametres.set('filiere', filiere)
  }

  const res = await api<{ data: ApercuDuConcours }>(`/me/concours/apercu?${parametres.toString()}`)

  return res.data
}

export function basculerDeConcours(
  slug: string,
): Promise<{ data: { actif: string; themes_suivis: number } }> {
  return api<{ data: { actif: string; themes_suivis: number } }>('/me/concours-actif', {
    method: 'PUT',
    body: JSON.stringify({ slug }),
  })
}

// ── Ce que le concours pèse — RG-1.13quinquies, [DECISIONS §116] ──────────

export interface ThematiquePesee {
  path: string
  nom: string
  part_pct: number
  /** Combien de fois le thème est tombé, tous sujets dépouillés confondus. */
  occurrences: number
  /** Sur combien de sujets. Sans lui, « 41 fois » ne dit pas s'il revient. */
  sujets: number
  /** RG-04-3 — sous trois sujets, la part est une tendance, pas une mesure. */
  indicatif: boolean
  suivable: boolean
}

export interface MatierePesee extends ThematiquePesee {
  thematiques: ThematiquePesee[]
}

/**
 * **Les niveaux ne s'additionnent PAS.** `tag_exam_weights` pèse tout l'arbre :
 * les matières somment à 100, leurs thématiques à ~98, et mêler les deux donne
 * 198 %. On affiche donc la matière, puis ses thématiques, jamais un total
 * mêlé (§116).
 */
export interface PoidsDuConcours {
  concours: string | null
  sujets_depouilles: number
  calcule_le: string | null
  recence: { demi_vie_annees: number; plancher: number }
  matieres: MatierePesee[]
}

export function poidsDuConcours(): Promise<{ data: PoidsDuConcours }> {
  return api<{ data: PoidsDuConcours }>('/me/concours/poids')
}
